import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ArrowRight, Mail, Phone } from "lucide-react";
import { PageHero } from "../components/common/PageHero";
import { Reveal } from "../components/common/Reveal";
import { api, formatApiError } from "../lib/api";
import { usePageMeta } from "../lib/usePageMeta";

const REASONS = ["EVENT BOOKING", "ORDER", "WHOLESALE", "GENERAL QUESTION"];
const inputCls = "w-full rounded-2xl bg-white border border-zinc-300 px-5 py-4 text-sm font-medium outline-none focus:ring-2 focus:ring-pink placeholder:text-zinc-400";
const labelCls = "block text-[11px] font-bold tracking-[0.25em] uppercase text-zinc-500 mb-2";

export const ContactPage = () => {
    usePageMeta("Contact — Showtime Carnival Co. | NYC", "Questions about events, orders or wholesale? Talk to the Showtime team.");
    const [form, setForm] = useState({ name: "", email: "", phone: "", reason: REASONS[0], message: "" });
    const [loading, setLoading] = useState(false);
    const [sent, setSent] = useState(false);
    const [biz, setBiz] = useState({ business_email: "", business_phone: "" });

    useEffect(() => {
        api.get("/settings/public")
            .then((res) => setBiz({ business_email: res.data?.business_email || "", business_phone: res.data?.business_phone || "" }))
            .catch(() => {});
    }, []);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            await api.post("/contact", form);
            setSent(true);
        } catch (err) {
            toast.error(formatApiError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <>
            <PageHero
                kicker="Contact"
                title="Talk To The Show."
                copy="Event questions, order help, wholesale or just saying hi — we read everything."
                dark={false}
                testid="contact-hero"
            />
            <section className="bg-paper py-16 md:py-24">
                <div className="mx-auto max-w-2xl px-5 md:px-10">
                    {(biz.business_email || biz.business_phone) && (
                        <Reveal>
                            <div className="mb-8 grid sm:grid-cols-2 gap-3" data-testid="contact-direct-info">
                                {biz.business_phone && (
                                    <a href={`tel:${biz.business_phone.replace(/[^+\d]/g, "")}`} data-testid="contact-phone-link" className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-5 py-4 hover:border-pink transition-colors">
                                        <Phone size={18} className="text-pink-dark shrink-0" />
                                        <span className="font-semibold text-sm">{biz.business_phone}</span>
                                    </a>
                                )}
                                {biz.business_email && (
                                    <a href={`mailto:${biz.business_email}`} data-testid="contact-email-link" className="flex items-center gap-3 rounded-2xl border border-zinc-200 bg-white px-5 py-4 hover:border-pink transition-colors">
                                        <Mail size={18} className="text-pink-dark shrink-0" />
                                        <span className="font-semibold text-sm truncate">{biz.business_email}</span>
                                    </a>
                                )}
                            </div>
                        </Reveal>
                    )}
                    <Reveal>
                        {sent ? (
                            <div className="bg-white rounded-3xl border border-zinc-200 p-10 text-center" data-testid="contact-confirmation">
                                <h2 className="font-oswald uppercase text-3xl">Message Received.</h2>
                                <p className="mt-3 text-zinc-500 text-sm">The Showtime team will get back to you shortly.</p>
                            </div>
                        ) : (
                            <form onSubmit={submit} data-testid="contact-form" className="bg-white rounded-3xl border border-zinc-200 p-7 md:p-10 grid gap-5">
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="ct-name" className={labelCls}>Name *</label>
                                        <input id="ct-name" required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ct-email" className={labelCls}>Email *</label>
                                        <input id="ct-email" type="email" required value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
                                    </div>
                                </div>
                                <div className="grid gap-5 sm:grid-cols-2">
                                    <div>
                                        <label htmlFor="ct-phone" className={labelCls}>Phone</label>
                                        <input id="ct-phone" type="tel" value={form.phone} onChange={set("phone")} className={inputCls} data-testid="contact-phone-input" />
                                    </div>
                                    <div>
                                        <label htmlFor="ct-reason" className={labelCls}>Reason *</label>
                                        <select id="ct-reason" value={form.reason} onChange={set("reason")} className={inputCls} data-testid="contact-reason-select">
                                            {REASONS.map((r) => <option key={r}>{r}</option>)}
                                        </select>
                                    </div>
                                </div>
                                <div>
                                    <label htmlFor="ct-msg" className={labelCls}>Message *</label>
                                    <textarea id="ct-msg" rows={5} required value={form.message} onChange={set("message")} className={inputCls} data-testid="contact-message-input" />
                                </div>
                                <button
                                    type="submit"
                                    disabled={loading}
                                    data-testid="contact-submit-button"
                                    className="inline-flex items-center justify-center gap-2 rounded-full bg-pink py-4 font-oswald uppercase tracking-widest text-ink hover:bg-ink hover:text-white transition-colors duration-300 active:scale-95 disabled:opacity-50"
                                >
                                    {loading ? "Sending..." : "Send Message"} <ArrowRight size={16} />
                                </button>
                            </form>
                        )}
                    </Reveal>
                </div>
            </section>
        </>
    );
};
